import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { selectPassengerArrangement } from '@shared/base-search-bar/store/selectors/passenger-arrangement.selector';
import * as bookingActions from '@booking/store/actions/booking.actions';
import * as paxActions from '@pax/store/actions/pax.actions';
import {
  AirBookingPayload, AutoBookingPayload, HotelBookingPayload, PackageBookingPayload
} from '@booking/entities/booking-arrangement';
import { User } from '@app/modules/core/interfaces/user';
import { AirValidationBySectorPayload, AirValidationPayload } from '@booking/entities/air-validation.payload';
import {
  AirValidationBySectorResponse,
  AirValidationResponse,
  AirValidationResponseError
} from '@app/entities/air-validation.response';
import { bookingRootSelector } from '@booking/store/selectors/booking-root.selector';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { getUiBookingState } from '@booking/store/selectors/ui.selector';
import { UiBookingState } from '@booking/store/state/booking.state';
import { selectFlightClass } from '@shared/base-search-bar/store/selectors/flight-class.selector';
import { HotelValidationPayload, ByHoursData } from '@booking/entities/hotel-validation.payload';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { selectGuestsPerRoom } from '@shared/base-search-bar/store/selectors/guests-per-room.selector';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { BillingResponse } from '@billing/entities/billing.payload';
import { billingRootSelector } from '@billing/store/selectors/billing-root.selector';
import { RappiPayment } from '@booking/entities/rappi-payment';
import { PackageValidationPayload } from '@booking/entities/package-validation.payload';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { VehicleValidatePayload } from '@autos/entities/vehicle-validate.payload';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { FlightItem } from '@app/entities/flight-results';
import { UpsellFacade } from '@upsell/upsell.facade';
import { flightKeysMapper } from '@flight-results/mappers/flight-keys.mapper';
import { UpsellPackagePayload } from '@packages/packages-result/entities/upsell';
import { FetchFlightsOptionPayload } from '@flight-results/entities/fetch-flights-option.payload';
import { isNull } from '@app/util/util';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import {
  ActivitiesBookPayloadVM, ActivitiesValidatePayload, ActivitiesValidateResponse
} from '@activities/entities/activities-booking';

@Injectable()
export class BookingFacade {

  constructor(
    private store: Store<State>,
    private upsellFacade: UpsellFacade
  ) { }

  public defaultBillingInfo$: Observable<BillingResponse> = this.store.pipe(
    select(billingRootSelector),
    map(state => state.defaultBilling)
  );

  public airValidation$: Observable<AirValidationResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state?.airValidation)
  );

  public airValidationBySector$: Observable<AirValidationBySectorResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state?.airValidationBySector)
  );

  public validationError$: Observable<AirValidationResponseError> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.validationError)
  );

  public hotelValidation$: Observable<HotelValidationResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.hotelValidation)
  );

  public autoValidation$: Observable<VehicleValidateResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.autoValidation)
  );

  public activitiesValidation$: Observable<ActivitiesValidateResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state?.activitiesValidation)
  );

  public countries$: Observable<string[]> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.countries)
  );

  public frequentFlyerPrograms$: Observable<FrequentFlyerResponse[]> = this.store.pipe(
    map(state => state.frequentFlyerProgram)
  );

  public flightClass$: Observable<FlightClass> = this.store
    .pipe(
      select(selectFlightClass)
    );

  public passengerArrangementsState$: Observable<PassengerArrangement[]> = this.store
    .pipe(
      select(selectPassengerArrangement)
    );

  public guestsPerRoomState$: Observable<Room[]> = this.store
    .pipe(
      select(selectGuestsPerRoom)
    );

  public isLoadingBooking$: Observable<boolean> = this.store
    .pipe(
      select(getUiBookingState),
      map((state: UiBookingState) => state.isLoadingBooking)
    );

  public isLoadingValidation$: Observable<boolean> = this.store
    .pipe(
      select(getUiBookingState),
      map((state: UiBookingState) => state.isLoadingValidation)
    );

  public paxSectionReady$: Observable<boolean> = this.store
    .pipe(
      select(getUiBookingState),
      map((state: UiBookingState) => state.paxSectionReady)
    );

  public bookingReady$: Observable<boolean> = this.store
    .pipe(
      select(getUiBookingState),
      map((state: UiBookingState) => state.bookingReady)
    );

  public user$: Observable<User> = this.store.select(store => store.user);

  public isChurningExceded$: Observable<boolean> = this.store
    .pipe(
      select(getUiBookingState),
      map((state: UiBookingState) => state.isChurningExceded)
    );

  public hasErrorInBooking$: Observable<boolean> = this.store
    .pipe(select(getUiBookingState), map((state: UiBookingState) => state.hasBookingError));

  public proceedToCheckout$: Observable<boolean> = this.store
    .pipe(select(getUiBookingState), map((state: UiBookingState) => state.proceedToCheckout));

  public exchangeRate$: Observable<number> = this.store.pipe(select(getUiBookingState), map(state => state.exchangeRate));

  public setBookingAirArrangement(payload: AirBookingPayload): void {
    this.store.dispatch(bookingActions.setBookingAirArrangementAction({ payload }));
  }

  public setBookingAirBySectorArrangement(payload: AirBookingPayload): void {
    this.store.dispatch(bookingActions.setBookingAirBySectorArrangementAction({ payload }));
  }

  public setBookingHotelArrangement(payload: HotelBookingPayload, searchBy: SearchBy): void {
    this.store.dispatch(bookingActions.setBookingHotelArrangementAction({ payload, searchBy }));
  }

  public setBookingAutoArrangement(payload: AutoBookingPayload): void {
    this.store.dispatch(bookingActions.setBookingAutoArrangementAction({ payload }));
  }

  public fetchAirValidation(payload: AirValidationPayload, user: User, isBuses: boolean): void {
    this.store.dispatch(bookingActions.fetchAirValidationAction({ payload, user, isBuses }));
  }

  public fetchAirValidationBySector(payload: AirValidationBySectorPayload, user: User): void {
    this.store.dispatch(bookingActions.fetchAirValidationBySectorAction({ payload, user }));
  }

  public fetchAirValidationErrorAction(typeError: AirValidationResponseError): void {
    this.store.dispatch(bookingActions.fetchAirValidationErrorAction({ typeError: typeError }));
  }

  public fetchHotelValidation(
    payload: HotelValidationPayload, 
    user: User, 
    byHoursData: ByHoursData): void {
    this.store.dispatch(bookingActions.fetchHotelValidationAction({ payload, user, byHoursData }));
  }

  public fetchAutoValidation(payload: VehicleValidatePayload, user: User): void {
    this.store.dispatch(bookingActions.fetchAutoValidationAction({ payload, user }));
  }

  public fetchCountries(): void {
    this.store.dispatch(bookingActions.fetchCountriesAction());
  }

  public resetAllBookValidationError(): void {
    this.store.dispatch(bookingActions.resetAllBookValidationErrorAction());
  }

  public bookingReadyControl(payload: boolean, isActiveBillingByControl: boolean, hasDefaultBilling: boolean = null): void {
    if (isActiveBillingByControl) {
      if (isNull(hasDefaultBilling)) {
        this.defaultBillingInfo$.pipe(take(1)).subscribe(defaultBillingInfo => {
          this.store.dispatch(bookingActions.bookingReadyControlAction({ payload: !!payload && !!defaultBillingInfo }));
        }).unsubscribe();
      } else this.store.dispatch(bookingActions.bookingReadyControlAction({ payload: !!payload && !!hasDefaultBilling }));
    } else this.store.dispatch(bookingActions.bookingReadyControlAction({ payload }));
  }

  public paxSectionReadyControl(payload: boolean): void {
    this.store.dispatch(bookingActions.paxSectionReadyControlAction({ payload }));
  }

  public resetValidators(): void {
    this.store.dispatch(bookingActions.resetValidatorsAction());
  }

  public fetchRappiPayment(payload: RappiPayment, searchBy: SearchBy): void {
    this.store.dispatch(bookingActions.fetchRappiPaymentAction({ payload, searchBy }));
  }

  public fetchRappiPaymentDesktopAction(payload: RappiPayment, searchBy: SearchBy): void {
    this.store.dispatch(bookingActions.fetchRappiPaymentDesktopAction({ payload, searchBy }));
  }

  public bookingChurningExceded(payload: boolean): void {
    this.store.dispatch(bookingActions.bookingChurningExcededAction({ payload }));
  }

  public fetchPackageValidation(payload: PackageValidationPayload, user: User): void {
    this.store.dispatch(bookingActions.fetchPackageValidationAction({ payload, user }));
  }

  public setBookingPackageArrangement(payload: PackageBookingPayload): void {
    this.store.dispatch(bookingActions.setBookingPackageArrangementAction({ payload }));
  }

  public setBookingActivitiesArrangement(payload: ActivitiesBookPayloadVM): void {
    this.store.dispatch(bookingActions.setBookingActivitiesArrangementAction({ payload }));
  }

  public setBookingType(payload: TravelBookingType): void {
    this.store.dispatch(paxActions.setBookingTypeAction({ payload }));
  }

  public proceedToCheckout(): void {
    this.store.dispatch(bookingActions.proceedToCheckoutAction());
  }

  public selectFlightsForUpsell(
    selectedFlights: FlightItem[], sessionToken: string, searchId: string, optionId: number
  ): void {
    const flightKeys = flightKeysMapper(selectedFlights);
    this.upsellFacade.fetchOptionForUpsellAction({
      SearchId: searchId,
      SessionToken: sessionToken,
      FlightKeys: flightKeys,
      AirlineCode: selectedFlights?.[0]?.AirlineCode,
      OptionId: optionId
    });
  }

  public selectFlightsPackagesUpsellForDesktop(flightsPayload: FetchFlightsOptionPayload,
    upsellPayload: UpsellPackagePayload): void {
    this.upsellFacade.fetchPackageUpsellAirDesktop(flightsPayload, upsellPayload);
  }

  public fetchActivitiesValidate(payload: ActivitiesValidatePayload, user: User): void {
    this.store.dispatch(bookingActions.fetchActivitiesValidateAction({ payload, user }));
  }
}
